import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { Reveal } from "./Reveal";
import { TID } from "../lib/testIds";

const MAP_EMBED =
  "https://www.google.com/maps?q=Langenlenstrasse+10b,+9100+Herisau,+Schweiz&output=embed";

export default function Contact() {
  const { t } = useLang();
  return (
    <section
      id="contact"
      data-testid={TID.contactSection}
      className="relative bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">
              — {t.contact.eyebrow}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-black tracking-tight mt-4 leading-tight">
              {t.contact.title}
            </h2>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-5 space-y-6">
            <ContactItem
              icon={Phone}
              label={t.contact.phone}
              testId={TID.contactPhone1}
              value="+41 78 321 52 49"
              href="tel:+41783215249"
            />
            <ContactItem
              icon={Phone}
              label={t.contact.phone}
              testId={TID.contactPhone2}
              value="+41 78 258 42 61"
              href="tel:+41782584261"
            />
            <ContactItem
              icon={Mail}
              label={t.contact.email}
              testId={TID.contactEmail}
              value="info@zemiswiss.ch"
              href="mailto:info@zemiswiss.ch"
            />
            <ContactItem
              icon={MapPin}
              label={t.contact.address}
              testId={TID.contactAddress}
              value="Langenlenstrasse 10b, 9100 Herisau, Schweiz"
              href="https://www.google.com/maps?q=Langenlenstrasse+10b,+9100+Herisau,+Schweiz"
            />
            <ContactItem
              icon={Clock}
              label={t.contact.hours}
              value={t.contact.hoursValue}
            />
          </Reveal>

          <Reveal delay={120} className="lg:col-span-7">
            <div className="relative aspect-[4/3] sm:aspect-[16/10] border border-black/10 overflow-hidden bg-zinc-100">
              <iframe
                src={MAP_EMBED}
                title={t.contact.mapTitle}
                data-testid={TID.contactMap}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="p-3 bg-black text-[#D4AF37] flex-shrink-0 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
          {label}
        </div>
        <div className="mt-1 text-lg sm:text-xl text-black">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        target={href.startsWith("http") ? "_blank" : undefined}
        rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
        className="block border-t border-black/10 pt-6 first:border-t-0 first:pt-0"
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId} className="border-t border-black/10 pt-6">
      {inner}
    </div>
  );
}
